import { useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";

import {
  STEAMBOT_REQUEST_ASSIGNMENT,
  STEAMBOT_STATUS,
} from "MVVM/GraphQL/steambot";
import { useUser } from "MVVM/Hooks/useUser";

const POLL_INTERVAL = 5000;

export type SteambotBot = { steamId64: string; profileUrl: string };
export type SteambotStatus = {
  eligible: boolean;
  enrolled: boolean;
  capacityAvailable: boolean;
  bot: SteambotBot | null;
};

export const useSteambot = () => {
  const { allstarUser } = useUser();
  const isLoggedIn = Boolean(allstarUser?.loggedIn);

  const { data, loading, startPolling, stopPolling } = useQuery(
    STEAMBOT_STATUS,
    {
      skip: !isLoggedIn,
      fetchPolicy: "network-only",
    },
  );
  const [requestAssignment, { data: assignmentData, loading: assigning }] =
    useMutation(STEAMBOT_REQUEST_ASSIGNMENT);

  const status: SteambotStatus | undefined = data?.steambotStatus;
  const enrolled = Boolean(status?.enrolled);
  const assignedBot: SteambotBot | null =
    assignmentData?.steambotRequestAssignment?.bot || status?.bot || null;

  // While the user has an assigned bot to add, poll until the friendship
  // shows up (Apollo doesn't fire onCompleted on poll refetches, so consumers
  // must react to `enrolled` from data, which they do).
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    if (assignedBot && !enrolled) {
      startPolling(POLL_INTERVAL);
      return () => stopPolling();
    }
    stopPolling();
    return undefined;
  }, [isLoggedIn, Boolean(assignedBot), enrolled, startPolling, stopPolling]);

  return {
    isLoggedIn,
    loading,
    status,
    enrolled,
    assignedBot,
    assigning,
    requestAssignment,
    showBubble:
      isLoggedIn &&
      Boolean(status?.eligible) &&
      !enrolled &&
      Boolean(status?.capacityAvailable),
  };
};
